import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Chart from 'react-google-charts';
import { FlexContainer } from 'common/components/atoms';
import colors from 'common/styles/colors';
import { chartOptions } from './constants';

const ChartContainer = styled(FlexContainer).attrs({
  direction: 'column',
  alignItems: 'center',
  width: '100%'
})`
  ${props => props.isInteractive && `
    & svg g path, & svg g text {
      cursor: pointer;
    }
  `}
  div.google-visualization-tooltip, div.google-visualization-tooltip * {
    pointer-events: none;
  }
  svg g { font-weight: 600; }
`;

const TITLE_HEIGHT = 40;
const Title = styled.div`
  color: ${colors.darkGray};
  font-weight: 600;
  width: 100%;
  padding: 1em 0 0 1em;
  height: ${TITLE_HEIGHT}px;
`;

const LineChart = ({
  title,
  data,
  colors,
  isInteractive,
  disableGrid
}) => {
  const { t } = useTranslation();
  const options = { ...chartOptions.LineChart, colors };

  if (disableGrid) {
    options.hAxis.minorGridlines = { count: 0 };
    options.vAxis.minorGridlines = { count: 0 };
  }

  const titleHeight = title !== '' ? TITLE_HEIGHT : 0;

  const containerRef = useRef();
  const [height, setHeight] = useState(0);
  const { current: resizeObserver } = useRef(new ResizeObserver((entries) => {
    entries.forEach((entry) => {
      setHeight(entry.target.offsetHeight);
    });
  }));

  useEffect(() => {
    resizeObserver.observe(containerRef.current.parentElement);
    return () => resizeObserver.disconnect();
  }, []);

  return (
    <ChartContainer
      innerRef={containerRef}
      isInteractive={isInteractive}
    >
      {title !== '' && <Title className="title">{title}</Title>}
      {data.length >= 1 ? (
        <Chart
          key={height}
          width="100%"
          height={height - titleHeight}
          chartType="LineChart"
          data={data}
          options={options}
        />
      ) :
        <h3>{t('widgetLibrary.dataUnavailable')}</h3>
      }
    </ChartContainer>
  );
};

LineChart.propTypes = {
  title: PropTypes.string,
  isInteractive: PropTypes.bool,
};

LineChart.defaultProps = {
  title: '',
  isInteractive: true,
};

export default LineChart;
